import { CollectionTarget } from "@ciaopeople/cp-shatsu/DomHelper";
import { EventHandler, SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { isBoolean } from "@ciaopeople/cp-shatsu/TypeChecking";
import { flatten, hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { GroupableData } from "../../api/commondata/GroupableData.js";
import { UserActionError, UserActionErrorCode, UserActionIdentifier } from "../../api/commondata/UserActionError.js";
import { RESTAPIEventNames, RESTAPIEvents } from "../../api/RESTAPIEvents.js";
import { RESTUserServices } from "../../api/RESTUserServices.js";
import { ContentRestrictionCriteria, isTopicQueryByExtendedId, isTopicQueryByShortId, isTopicQueryByUrl, isTopicQueryByWordpressId, isTopicQueryObjWithFollow, TopicQueryObj, TopicQueryObjWithFollow, UserActionExtraInfo } from "../../api/types/NotificationDataTypes.js";
import { AAABarrierWithSource } from "../aaa/AAABarrierWithSource.js";
import { ActionableEvents, AttachAndStatusData, ElementWithAttachData, ParseAndAttachModule } from "./ParseAndAttachModule.js";
import { SdkHandledHTMLElement, SubscriptionCallbackData } from "./SdkHandledHTMLElement.js";

type Action = (evt: string, data?: any) => void;

export class FollowHTMLElement extends SdkHandledHTMLElement<boolean> {
    
    
    public setStatus(s: boolean): SdkHandledHTMLElement<boolean> {
        this.attr("data-followed", s?"true":"false");
        return this;
    }
    public getStatus(): boolean {
        return this.firstAttr("data-followed") == "true";
    }

    public getUrl(): string {
        return this.firstAttr("data-url");
    }
 
    public getUTMSource(): string {
        const incomingSource = super.getUTMSource();
        return incomingSource && incomingSource != ""?incomingSource:"follow";
    }
    
    public getAssociatedUserAction(): UserActionIdentifier {
        return UserActionIdentifier.FOLLOW;
    }


}


class GroupableTopicQueryObj implements GroupableData {


    constructor(public readonly query: TopicQueryObj) {

    }

    /**
     * 
     * @returns null se non matcha nessun tipo conosciuto di topic query
     */
    getGroupName(): string {
        if(isTopicQueryByUrl(this.query)) {
            return `[url]-${this.query.url}`;
        }
        else if(isTopicQueryByExtendedId(this.query)) {
            return `[extid]-${this.query.extId}`;
        }
        else if(isTopicQueryByShortId(this.query)) {
            return `[sid]-${this.query.hint}-${this.query.id}`;
        }
        else if(isTopicQueryByWordpressId(this.query)) {
            return `[wpid]-${this.query.hint}-${this.query.wpid}`;
        }
        else return null;
    }


}


export interface TopicFollowEventWithInvolvedDOM extends TopicQueryObjWithFollow {
    dom: HTMLElement[]
}

export function isTopicFollowEventWithInvolvedDOM(obj: any): obj is TopicFollowEventWithInvolvedDOM {
    return isTopicQueryObjWithFollow(obj) && hasOwnProperty(obj, "dom");
}


export class FollowParseAndAttachModule extends ParseAndAttachModule<boolean, GroupableTopicQueryObj> implements EventHandler {
   

    private eh: EventHandler;


    constructor(private restServices: AAABarrierWithSource<SdkHandledHTMLElement<any>, RESTUserServices>, 
                private restEvents: RESTAPIEvents) {
        super();
        this.eh = new SimpleEventHandler();
        this.restEvents.onTopicFollowStatusChange((followData) => {
            if(isTopicQueryByUrl(followData.query)) {
                this.updatedElementStatuses(
                    [{
                        attachData: new GroupableTopicQueryObj(followData.query),
                        status: followData.followed,
                        restriction: followData.restriction?followData.restriction:ContentRestrictionCriteria.ACCESSIBLE_TO_ALL
                    }]                
                );
            }   
        });
    }

    

    protected extractAndCreate(el: HTMLElement): ElementWithAttachData<boolean, GroupableTopicQueryObj> {
        const parsed = new FollowHTMLElement(el);
        if(parsed.getUrl() != "") {
            return {
                element: parsed,
                attachData: new GroupableTopicQueryObj({
                    url: parsed.getUrl()
                })
            };
        }
        else return null;
    }

    public action(el: ElementWithAttachData<boolean, GroupableTopicQueryObj>): void {
        this.restServices.executeWithBarrierWithSource(el.element, service => {
            const followElement = el.element;
            const followElemStatus = followElement.getStatus();
            const umed = followElement.getUTMmedium();
            const maybeExtraInfo: UserActionExtraInfo = umed && umed != ""?{ source_element: umed }:null;
            
            const [actionPromise,isNoOp] = followElemStatus?
                (el.element.isSubscriptionOnly()?[Promise.resolve(true),true]:[service.unfollow(el.attachData.query, maybeExtraInfo),false]):
                [service.follow(el.attachData.query, maybeExtraInfo),false];          
           
            return actionPromise.then(res =>  {
                const newFollowStatus = isBoolean(res)?res:(res.data.topic.followed?true:false);
                followElement.setStatus(newFollowStatus);
                
                if(!isNoOp) {
                    this.success.next( {
                        ...el,
                        action: UserActionIdentifier.FOLLOW
                    });
                }
                return newFollowStatus;
            }).then(res => {
                if(res) {
                    return service.getMe(true).then(uresp => {
                        const data: SubscriptionCallbackData = {
                            subscription_intent: res,
                            is_subscription_changed: followElemStatus != res,
                            user: {
                                id: uresp.data.id,
                                first_name: uresp.data.nameAndGender?.first_name,
                                last_name: uresp.data.nameAndGender?.last_name,
                                email: uresp.data.email                        
                            },
                            redirect_url: el.element.getRedirectUrl(),
                            topic_label: followElement.getTopicLabel(),
                            user_was_already_registered: true
                        };   
                        followElement.executeAfterSubscriptionCallback(data);
                        return res;
                    });                    
                }
                else return Promise.resolve(res);
            }).catch(err => {
                const uaErr: UserActionError = {
                    code: UserActionErrorCode.UNKNOWN_ERROR,
                    action: UserActionIdentifier.FOLLOW,
                    message: err,
                    data: {
                        query: el.attachData
                    }
                };
                if(!isNoOp) {
                    this.errors.next(uaErr);
                }
                return Promise.reject(uaErr);
            });
            
        });
        
    }


    public parseAndAttach(el: CollectionTarget | CollectionTarget[], _trigger?: never): Promise<void> {
        return super.parseAndAttach(el, ActionableEvents.click);
    }


    

    protected afterAttach(newlyAttached: ElementWithAttachData<boolean, GroupableTopicQueryObj>[]): Promise<void> {
        if(newlyAttached && newlyAttached.length > 0) {
            const checkReq = {
                queries: newlyAttached.map(el => el.attachData.query)
            };
            return this.restServices.getBarricadedService().checkFollowedTopics(checkReq).then();
        }
        else return Promise.resolve();
    }

    protected afterUpdate(s: AttachAndStatusData<boolean, GroupableTopicQueryObj>, involvedElements: SdkHandledHTMLElement<boolean>[]): void {
        this.triggerTopicFollowStatusChange(         
            {
                dom: flatten(involvedElements.map(ie => ie.ig())),
                query: s.attachData.query,
                followed: s.status,
                restriction: involvedElements && involvedElements.length > 0?involvedElements[0].getContentRestrictionCriteria():ContentRestrictionCriteria.ACCESSIBLE_TO_ALL
            }
        );
    }


    private triggerTopicFollowStatusChange(evt: TopicFollowEventWithInvolvedDOM): void {
        this.trigger(RESTAPIEventNames.TOPIC_FOLLOW_STATUS_CHANGE, evt);
    }


    public onTopicFollowStatusChange(fun: (evt: TopicFollowEventWithInvolvedDOM) => any): void {
        this.on(RESTAPIEventNames.TOPIC_FOLLOW_STATUS_CHANGE, (evt, data) => {
            if(isTopicFollowEventWithInvolvedDOM(data)) fun(data);
        });
    }

    on(eventName: string, callback: Action) {
        this.eh.on(eventName, callback);
    }
    off(act: Action);
    off(evt: string, act: Action);
    off(evtOrAct: string | Action, act?: Action);
    off(evtOrAct: string | Action, act?: Action): any {
        return this.eh.off(evtOrAct, act);    
    }
    trigger(eventName: string, data: any): void {
        this.eh.trigger(eventName, data);
    }
    one(eventName: string, callback: Action): void {
        this.eh.one(eventName, callback);
    }

}